import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import styled from "@emotion/styled";
import { useNavigate, useLocation } from "react-router-dom";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import {
  Alert as MuiAlert,
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent,
  CircularProgress,
  TextField as MuiTextField,
  Grid,
  CardHeader,
  Typography,
  Tabs,
  Tab,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { spacing } from "@mui/system";

import SaveCancel from "../../../components/SaveCancel";
import usePlanGroup from "../../../hooks/usePlanGroup";
import TabPanel from "../../../components/TabPanel";
import { a11yProps } from "../../../utils/common";
import BasicDetail from "./components/BasicDetail";
import { showSuccessSnackbar, showErrorSnackbar } from "../../../redux/slices/snackbar";
import AllowedPlan from "./components/AllowedPlan";
import usePlan from "../../../hooks/usePlan";
import AssignOttPlans from "./components/AssignOttPlans";
import AllowedOttPlan from "./components/AllowedOttPlan";

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Card = styled(MuiCard)(spacing);

const Alert = styled(MuiAlert)(spacing);

const TextField = styled(MuiTextField)(spacing);

const validationSchema = Yup.object().shape({
  planGroupName: Yup.string().required("Required"),
  description: Yup.string().required("Required"),
  status: Yup.string().required("Required"),
  serviceType: Yup.string().required("Required"),
  sharingType: Yup.string().required("Required"),
  msoSharing: Yup.number().when("isSharingAllowed", {
    is: true,
    then: () =>
      Yup.number()
        .typeError("MSO sharing must be a number")
        .min(0, "Minimum value should be 0")
        .max(100, "Maximum value should be 100")
        .required("MSO Sharing is required"),
  }),
  distSharing: Yup.number().when("isSharingAllowed", {
    is: true,
    then: () =>
      Yup.number()
        .typeError("Dist sharing must be a number")
        .min(0, "Minimum value should be 0")
        .max(100, "Maximum value should be 100")
        .required("Distributor Sharing is required"),
  }),
  lcoSharing: Yup.number().when("isSharingAllowed", {
    is: true,
    then: () =>
      Yup.number()
        .typeError("LCO sharing must be a number")
        .min(0, "Minimum value should be 0")
        .max(100, "Maximum value should be 100")
        .required("LCO Sharing is required"),
  }),

  ottMsoSharing: Yup.number().when(["isSharingAllowed", "serviceType"], {
    is: (isSharingAllowed, serviceType) =>
      isSharingAllowed === true && serviceType === "INTERNET_OTT",
    then: () =>
      Yup.number()
        .typeError("MSO sharing must be a number")
        .min(0, "Minimum value should be 0")
        .max(100, "Maximum value should be 100")
        .required("OTT MSO Sharing is required"),
  }),
  ottDistSharing: Yup.number().when(["isSharingAllowed", "serviceType"], {
    is: (isSharingAllowed, serviceType) =>
      isSharingAllowed === true && serviceType === "INTERNET_OTT",
    then: () =>
      Yup.number()
        .typeError("Dist sharing must be a number")
        .min(0, "Minimum value should be 0")
        .max(100, "Maximum value should be 100")
        .required("OTT Distributor Sharing is required"),
  }),
  ottLcoSharing: Yup.number().when(["isSharingAllowed", "serviceType"], {
    is: (isSharingAllowed, serviceType) =>
      isSharingAllowed === true && serviceType === "INTERNET_OTT",
    then: () =>
      Yup.number()
        .typeError("LCO sharing must be a number")
        .min(0, "Minimum value should be 0")
        .max(100, "Maximum value should be 100")
        .required("OTT LCO Sharing is required"),
  }),
});

function BasicForm(props) {
  const dispatch = useDispatch();
  const form = React.useRef();

  const methods = useForm({
    resolver: yupResolver(validationSchema),
  });

  const { reset, handleSubmit, setValue, watch } = methods;

  const [assignedToId, setAssignedToId] = useState(null);

  const { getPlanGroup, planGroup, updatePlanGroup, isLoading } = usePlanGroup();
  const { plan } = usePlan();
  React.useEffect(() => {
    console.log(planGroup, " values: formValues,");

    if (Object.keys(planGroup).length !== 0) {
      const initialValues = {
        planGroupId: planGroup.planGroupId,
        planGroupName: planGroup.planGroupName,
        description: planGroup.description,
        status: planGroup.status,
        isSharingAllowed: planGroup.isSharingAllowed,
        ottPlans: planGroup.ottPlans,
        msoSharing: planGroup.msoSharing,
        distSharing: planGroup.distSharing,
        lcoSharing: planGroup.lcoSharing,
        ottDistSharing: planGroup.ottDistSharing,
        ottMsoSharing: planGroup.ottMsoSharing,
        ottLcoSharing: planGroup.ottLcoSharing,
        serviceType: planGroup.serviceType,
        sharingType: planGroup.sharingType,
        planGroupPlans: planGroup.planGroupPlans,
        planGroupOttPlans: planGroup.planGroupOttPlans,
      };

      reset(initialValues);
      setAssignedToId(planGroup?.assignedTo?.tenantId);
    }
  }, [planGroup]);

  const [tabValue, setTabValue] = React.useState(0);
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  const { state } = useLocation();
  console.log(state, "location");
  const { t } = props;

  const isSharingAllowed = watch("isSharingAllowed");

  const serviceType = watch("serviceType");
  const selectedOttPlans = watch("ottPlans");
  console.log(selectedOttPlans, "selectedOttPlans");

  React.useEffect(() => {
    if (form.current) {
      const { values, errors } = form.current;
      console.log(errors, "i am here to update values");
      dispatch({
        type: "UPDATE_FORM",
        payload: {
          id: "branch",
          data: { values, errors },
        },
      });
    }
  }, [tabValue]);

  const navigate = useNavigate();

  const onSubmit = async (data) => {
    try {
      await updatePlanGroup(data);
      dispatch(showSuccessSnackbar("Plan group updated successfully!"));
      //setStatus({ sent: true });
      //reset();
      cancelBtn();
    } catch (err) {
      //setStatus({ sent: false });
      //setErrorSubmit({ submit: err.message });
      dispatch(showErrorSnackbar(err.message));
    }
  };

  const cancelBtn = () => {
    navigate("/radius/plan-group/list");
  };

  const breadCrump = () => {
    return (
      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Typography>Radius</Typography>
        <Typography>Plan Group</Typography>
        <Typography>Edit</Typography>
      </Breadcrumbs>
    );
  };

  const fetchData = async () => {
    await getPlanGroup(state.id);
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <React.Fragment>
      <FormProvider {...methods}>
        <Card mb={6}>
          <CardHeader title="Edit Plan Group" subheader={breadCrump()} />
          <CardContent>
            {/* {status && status.sent && (
              <Alert severity="success" my={3}>
                Branch updated successfully!
              </Alert>
            )} */}
            {/* {errorSubmit && errorSubmit.submit && (
              <Alert severity="error" my={3}>
                {errorSubmit.submit}
              </Alert>
            )} */}

            {isLoading ? (
              <Box display="flex" justifyContent="center" my={6}>
                <CircularProgress />
              </Box>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)}>
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                  <Tabs value={tabValue} onChange={handleTabChange} aria-label="Plan Group">
                    <Tab label="Basic Details" {...a11yProps(0)} />
                    <Tab label="Allowed Internet Plans" {...a11yProps(1)} />
                    <Tab label="Alowed OTT Plans" {...a11yProps(2)} />
                  </Tabs>
                  <TabPanel value={tabValue} index={0}>
                    <BasicDetail validationFields={validationSchema.fields} />
                  </TabPanel>
                  <TabPanel value={tabValue} index={1}>
                    <AllowedPlan
                      planGroupId={state?.id}
                      isSharingAllowed={isSharingAllowed}
                      serviceType={serviceType}
                      validationFields={validationSchema.fields}
                    />
                  </TabPanel>
                  <TabPanel value={tabValue} index={2}>
                    <AllowedOttPlan
                      planGroupId={state?.id}
                      isSharingAllowed={isSharingAllowed}
                      serviceType={serviceType}
                      validationFields={validationSchema.fields}
                    />
                  </TabPanel>
                  <SaveCancel cancelBtn={cancelBtn} isLoading={isLoading} />
                </Box>
              </form>
            )}
          </CardContent>
        </Card>
      </FormProvider>
    </React.Fragment>
  );
}

function EditPlanGroup() {
  const { t } = useTranslation();
  return (
    <React.Fragment>
      <BasicForm t={t} />
    </React.Fragment>
  );
}

export default EditPlanGroup;
